import React, { createContext, useContext, useState, useEffect } from 'react';
import type { ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import type { 
  User, 
  Company, 
  AuthState, 
  Permission, 
  Module
} from '../types';
import { UserType, Status } from '../types';
import { mockUsers, mockCompanies, mockModules } from '../data/mockData';

interface AuthContextType extends AuthState {
  login: (email: string, password: string) => Promise<void>;
  logout: () => void;
  switchCompany: (companyId: string) => void;
  hasPermission: (permissionCode: string) => boolean;
  hasModule: (moduleCode: string) => boolean;
  isSuperAdmin: () => boolean;
  isCompanyAdmin: () => boolean;
  updateUser: (user: Partial<User>) => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const navigate = useNavigate();
  const [authState, setAuthState] = useState<AuthState>({
    isAuthenticated: false,
    user: null,
    company: null,
    token: null,
    refreshToken: null,
    permissions: [],
    modules: []
  });

  // Cargar sesión desde localStorage al iniciar
  useEffect(() => {
    const loadSession = () => {
      const token = localStorage.getItem('authToken');
      const userData = localStorage.getItem('user');
      const companyData = localStorage.getItem('company');
      
      if (token && userData) {
        try {
          const user = JSON.parse(userData);
          const company = companyData ? JSON.parse(companyData) : null;
          
          // Obtener permisos y módulos basados en el usuario
          const permissions = getUserPermissions(user);
          const modules = getUserModules(user, company);
          
          setAuthState({
            isAuthenticated: true,
            user,
            company,
            token,
            refreshToken: localStorage.getItem('refreshToken') || null,
            permissions,
            modules
          });
        } catch (error) {
          console.error('Error loading session:', error);
          logout();
        }
      }
    };
    
    loadSession();
  }, []);

  // Función para obtener permisos del usuario
  const getUserPermissions = (user: User): Permission[] => {
    const permissions: Permission[] = [];
    
    if (user.type === UserType.SUPER_ADMIN) {
      // Super admin tiene todos los permisos
      return getAllPermissions();
    }
    
    // Obtener permisos de los roles del usuario
    user.roles?.forEach(role => {
      role.permissions?.forEach(permission => {
        if (!permissions.find(p => p.id === permission.id)) {
          permissions.push(permission);
        }
      });
    });
    
    return permissions;
  };

  // Función para obtener todos los permisos (para super admin)
  const getAllPermissions = (): Permission[] => {
    // En producción esto vendría de la API
    return [
      {
        id: '1',
        code: 'companies.manage',
        name: 'Gestionar Empresas',
        description: 'Crear, editar y eliminar empresas',
        module: 'companies',
        actions: ['create', 'read', 'update', 'delete']
      },
      {
        id: '2',
        code: 'users.manage',
        name: 'Gestionar Usuarios',
        description: 'Administrar usuarios del sistema',
        module: 'users',
        actions: ['create', 'read', 'update', 'delete']
      },
      {
        id: '3',
        code: 'roles.manage',
        name: 'Gestionar Roles',
        description: 'Administrar roles y permisos',
        module: 'roles',
        actions: ['create', 'read', 'update', 'delete']
      }
    ];
  };

  // Función para obtener módulos del usuario
  const getUserModules = (user: User, company: Company | null): Module[] => {
    if (user.type === UserType.SUPER_ADMIN) {
      return mockModules;
    }
    
    if (!company) return [];
    
    // Filtrar módulos basados en el plan de la empresa
    return mockModules.filter(module => {
      // Verificar si el módulo está disponible para el plan de la empresa
      return module.requiredPlan.includes(company.plan);
    });
  };

  // Función de login
  const login = async (email: string, _password: string): Promise<void> => {
    try {
      // Simulación de login - buscar usuario en mock data
      const user = mockUsers.find(u => u.email === email && u.status === Status.ACTIVE);
      
      if (!user) {
        throw new Error('Credenciales inválidas');
      }
      
      // Obtener empresa del usuario (si no es super admin)
      let company: Company | null = null;
      if (user.companyId) {
        company = mockCompanies.find(c => c.id === user.companyId) || null;
      }
      
      // Generar tokens (simulado)
      const token = `jwt-token-${Date.now()}`;
      const refreshToken = `refresh-token-${Date.now()}`;
      
      // Obtener permisos y módulos
      const permissions = getUserPermissions(user);
      const modules = getUserModules(user, company);
      
      // Guardar en localStorage
      localStorage.setItem('authToken', token);
      localStorage.setItem('refreshToken', refreshToken);
      localStorage.setItem('user', JSON.stringify(user));
      if (company) {
        localStorage.setItem('company', JSON.stringify(company));
      }
      
      // Actualizar estado
      setAuthState({
        isAuthenticated: true,
        user,
        company,
        token,
        refreshToken,
        permissions,
        modules
      });
      
      toast.success('¡Bienvenido!');
      
      // Redireccionar según el tipo de usuario
      if (user.type === UserType.SUPER_ADMIN) {
        navigate('/admin/companies');
      } else {
        navigate('/dashboard');
      }
    } catch (error: any) {
      toast.error(error.message || 'Error al iniciar sesión');
      throw error;
    }
  };

  // Función de logout
  const logout = () => {
    localStorage.removeItem('authToken');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('user');
    localStorage.removeItem('company');
    
    setAuthState({
      isAuthenticated: false,
      user: null,
      company: null,
      token: null,
      refreshToken: null,
      permissions: [],
      modules: []
    });
    
    navigate('/login');
    toast.info('Sesión cerrada');
  };

  // Función para cambiar de empresa (para usuarios con múltiples empresas)
  const switchCompany = (companyId: string) => {
    const company = mockCompanies.find(c => c.id === companyId);
    if (company && authState.user) {
      const modules = getUserModules(authState.user, company);
      
      localStorage.setItem('company', JSON.stringify(company));
      
      setAuthState(prev => ({
        ...prev,
        company,
        modules
      }));
      
      toast.success(`Cambiado a ${company.name}`);
      navigate('/dashboard');
    }
  };

  // Verificar si tiene un permiso específico
  const hasPermission = (permissionCode: string): boolean => {
    if (authState.user?.type === UserType.SUPER_ADMIN) return true;
    return authState.permissions.some(p => p.code === permissionCode);
  };

  // Verificar si tiene acceso a un módulo
  const hasModule = (moduleCode: string): boolean => {
    if (authState.user?.type === UserType.SUPER_ADMIN) return true;
    return authState.modules.some(m => m.code === moduleCode);
  };

  // Verificar si es super admin
  const isSuperAdmin = (): boolean => {
    return authState.user?.type === UserType.SUPER_ADMIN;
  };

  // Verificar si es admin de empresa
  const isCompanyAdmin = (): boolean => {
    return authState.user?.type === UserType.COMPANY_ADMIN;
  };

  // Actualizar datos del usuario
  const updateUser = (userData: Partial<User>) => {
    if (authState.user) {
      const updatedUser = { ...authState.user, ...userData };
      localStorage.setItem('user', JSON.stringify(updatedUser));
      setAuthState(prev => ({
        ...prev,
        user: updatedUser
      }));
    }
  };

  const value: AuthContextType = {
    ...authState,
    login,
    logout,
    switchCompany,
    hasPermission,
    hasModule,
    isSuperAdmin,
    isCompanyAdmin,
    updateUser
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};